import React, { useState } from "react";
import { ChevronDown, ChevronRight, AlertTriangle, CheckCircle2 } from "lucide-react";

/**
 * Generic collapsible section card.
 * When collapsed shows a summary line + optional missing-field chips.
 * Used throughout Receiving / Job pages to keep long forms scannable.
 *
 * Props:
 *  - title: section title (string or node)
 *  - icon: lucide-react icon component (optional)
 *  - summary: string | node — shown when collapsed (the captured key fields)
 *  - missing: string[] — labels of required fields still empty (highlighted red)
 *  - defaultOpen: bool (default true)
 *  - badge: optional node rendered next to the title
 *  - actions: optional node rendered on the right (e.g. an "Edit" button)
 *  - testid: data-testid root
 *  - tone: "default" | "accent" | "muted"  — visual treatment of the header
 *  - children: expanded content
 */
export function Collapsible({
  title, icon: Icon,
  summary, missing = [],
  defaultOpen = true, badge, actions,
  testid, tone = "default",
  children,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const isComplete = missing.length === 0;
  const headerBg =
    tone === "accent" ? "var(--card-2)" :
    tone === "muted" ? "var(--bg-soft, #fafafa)" :
    "var(--card)";
  const accentBorder = tone === "accent" ? "3px solid var(--gold-deep)" : undefined;
  return (
    <div className="card" style={{ marginBottom: 12, borderLeft: accentBorder }} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={testid ? `${testid}-toggle` : undefined}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 10,
          padding: "12px 14px",
          background: headerBg, border: "none", cursor: "pointer",
          borderRadius: open ? "var(--r,9px) var(--r,9px) 0 0" : "var(--r,9px)",
          textAlign: "left",
        }}>
        <span style={{ display: "inline-flex", alignItems: "center", color: "var(--text-2)" }}>
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </span>
        {Icon && <Icon size={15} color="var(--gold-deep)" />}
        <span className="t-strong" style={{ fontSize: 14, flex: "none" }}>{title}</span>
        {badge}
        {!open && (
          <span
            data-testid={testid ? `${testid}-summary` : undefined}
            className="t-mut"
            style={{
              fontSize: 12, marginLeft: 6,
              flex: 1, minWidth: 0,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
            {summary || <span style={{ fontStyle: "italic" }}>No data captured</span>}
          </span>
        )}
        {!open && missing.length > 0 && (
          <span style={{ display: "inline-flex", gap: 4, marginLeft: "auto", flexWrap: "wrap" }} data-testid={testid ? `${testid}-missing` : undefined}>
            {missing.slice(0, 4).map((m) => (
              <span key={m} className="badge" style={{
                background: "var(--red-bg, #fde7e7)",
                color: "var(--red, #b3261e)",
                fontSize: 10.5, fontWeight: 600, gap: 4,
              }}>
                <AlertTriangle size={10} /> {m}
              </span>
            ))}
            {missing.length > 4 && (
              <span className="badge" style={{ background: "var(--red-bg, #fde7e7)", color: "var(--red, #b3261e)", fontSize: 10.5 }}>
                +{missing.length - 4}
              </span>
            )}
          </span>
        )}
        {!open && missing.length === 0 && summary && (
          <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", color: "var(--green, #1f8a4d)" }} title="Section complete">
            <CheckCircle2 size={14} />
          </span>
        )}
        {open && actions && (
          <span style={{ marginLeft: "auto", display: "inline-flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
            {actions}
          </span>
        )}
      </button>
      {open && (
        <div style={{ padding: "12px 14px 14px", borderTop: "1px solid var(--line)" }}>
          {children}
        </div>
      )}
    </div>
  );
}
